import React, { useState } from 'react';
import { Button, Card, DataTable, Input, Badge, Modal } from '../../ui';
import type { PaginatedResponse } from '../../../types';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../../utils/dateUtils';

// ===============================
// INTERFACES GENÉRICAS
// ===============================

export interface Column<T> {
  key: keyof T | string;
  title: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface Filter {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'date';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface CrudTableProps<T> {
  // Datos
  data: PaginatedResponse<T> | undefined;
  isLoading: boolean;
  error: Error | null;
  
  // Configuración de columnas
  columns: Column<T>[];
  
  // Filtros
  filters?: Filter[];
  currentFilters: Record<string, any>;
  onFiltersChange: (filters: Record<string, any>) => void;
  
  // Paginación
  onPageChange: (page: number) => void;
  
  // Acciones
  onAdd?: () => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onView?: (item: T) => void;
  customActions?: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: (item: T) => void;
    className?: string;
    visible?: (item: T) => boolean;
  }>;
  
  // Configuración
  title: string;
  addButtonText?: string;
  deleteConfirmMessage?: (item: T) => string;
  
  // Permisos
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canView?: boolean;
}

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export function CrudTable<T extends Record<string, any>>({
  data,
  isLoading,
  error,
  columns,
  filters = [],
  currentFilters,
  onFiltersChange,
  onPageChange,
  onAdd,
  onEdit,
  onDelete,
  onView,
  customActions = [],
  title,
  addButtonText = 'Agregar',
  deleteConfirmMessage,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canView = true,
}: CrudTableProps<T>) {
  const { t } = useTranslation();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item: T | null }>({
    isOpen: false,
    item: null,
  });

  // ===============================
  // MANEJO DE FILTROS
  // ===============================

  const handleFilterChange = (key: string, value: any) => {
    onFiltersChange({
      ...currentFilters,
      [key]: value || undefined,
      page: 1, // Reset a primera página al filtrar
    });
  };

  const clearFilters = () => {
    onFiltersChange({ page: 1 });
  };

  // ===============================
  // ACCIONES
  // ===============================

  const handleDelete = (item: T) => {
    setDeleteModal({ isOpen: true, item });
  };

  const confirmDelete = () => {
    if (deleteModal.item && onDelete) {
      onDelete(deleteModal.item);
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  // ===============================
  // COLUMNAS CON ACCIONES
  // ===============================

  const columnsWithActions: Column<T>[] = [
    ...columns,
    {
      key: 'acciones',
      title: t('common.actions'),
      width: '120px',
      render: (_, item) => (
        <div className="flex items-center space-x-2">
          {customActions
            .filter(action => !action.visible || action.visible(item))
            .map((action) => (
            <Button
              key={action.key}
              variant="ghost"
              size="sm"
              onClick={() => action.onClick(item)}
              className={action.className || "text-gray-600 hover:text-gray-700"}
              title={action.label}
            >
              {action.icon}
            </Button>
          ))}
          {canView && onView && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(item)}
              className="text-blue-600 hover:text-blue-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Button>
          )}
          {canEdit && onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(item)}
              className="text-indigo-600 hover:text-indigo-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Button>
          )}
          {canDelete && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(item)}
              className="text-red-600 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </Button>
          )}
        </div>
      ),
    },
  ];

  // ===============================
  // ESTADO DE CARGA Y ERROR
  // ===============================

  if (error) {
    return (
      <Card>
        <div className="p-6 text-center">
          <div className="text-red-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('errors.load_error')}</h3>
          <p className="text-gray-500">{error.message}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-600 mt-1">
            {data !== undefined ? `${data.total} ${t('common.records_found')}` : t('common.loading')}
          </p>
        </div>
        {onAdd && (
          <Button 
            onClick={canAdd ? onAdd : undefined}
            disabled={!canAdd}
            variant={canAdd ? 'primary' : 'secondary'}
            title={!canAdd ? t('common.no_permission_create') : undefined}
            className="flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{addButtonText}</span>
          </Button>
        )}
      </div>

      {/* Filtros */}
      {filters.length > 0 && (
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('common.filter')}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
              >
                {t('common.clear_filters')}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filters.map((filter) => (
                <div key={filter.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {filter.label}
                  </label>
                  {filter.type === 'select' ? (
                    <select
                      value={currentFilters[filter.key] || ''}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('common.all')}</option>
                      {filter.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : filter.type === 'date' ? (
                    <input
                      type="date"
                      value={currentFilters[filter.key] || ''}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <Input
                      type={filter.type}
                      value={currentFilters[filter.key] || ''}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      placeholder={filter.placeholder}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <DataTable
          columns={columnsWithActions}
          data={data?.items || []}
          isLoading={isLoading}
          pagination={{
            currentPage: data?.page || 1,
            totalPages: data?.pages || 1,
            totalItems: data?.total || 0,
            itemsPerPage: data?.per_page || 10,
            onPageChange,
          }}
        />
      </Card>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        title={t('common.confirm_delete_title')}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {deleteModal.item && deleteConfirmMessage 
              ? deleteConfirmMessage(deleteModal.item)
              : t('common.confirm_delete_msg')
            }
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="ghost"
              onClick={() => setDeleteModal({ isOpen: false, item: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ===============================
// UTILIDADES PARA COLUMNAS COMUNES
// ===============================

export const estadoColumn = <T,>(): Column<T> => ({
  key: 'estado',
  title: 'Estado',
  width: '100px',
  render: (estado: string) => (
    <Badge variant={estado === 'activo' ? 'success' : 'secondary'}>
      {estado === 'activo' ? 'Activo' : 'Inactivo'}
    </Badge>
  ),
});
// Note: estadoColumn badge text uses fixed strings; admin views translate status labels directly

export const fechaColumn = <T,>(key: keyof T, title: string): Column<T> => ({
  key,
  title,
  width: '120px',
  render: (fecha: string) => {
    if (!fecha) return '-';
    return formatDateSafe(fecha);
  },
});

export const numeroColumn = <T,>(key: keyof T, title: string, decimales: number = 0): Column<T> => ({
  key,
  title,
  width: '100px',
  render: (valor: number) => {
    if (valor == null) return '-';
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    }).format(valor);
  },
});

export default CrudTable;
